import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getPackages, getPackagePostAward, updatePostAward } from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';
import AuditTrail from '../components/AuditTrail';

function timeAgo(date) {
  if (!date) return '—';
  const diff = Date.now() - new Date(date).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function daysUntil(date) {
  if (!date) return null;
  const diff = new Date(date) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function DueBadge({ date, status }) {
  if (status === 'complete') return <span className="badge badge-success">✓ Complete</span>;
  if (!date) return <span className="badge badge-neutral">No date set</span>;
  const days = daysUntil(date);
  if (days < 0) return <span className="badge badge-error">Overdue {Math.abs(days)}d</span>;
  if (days === 0) return <span className="badge badge-warning">Due today</span>;
  if (days <= 3) return <span className="badge badge-warning">Due in {days}d</span>;
  return <span className="badge badge-neutral">Due {new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>;
}

const POST_AWARD_STAGES = [
  { key: 'contract_po', statusKey: 'contract_po_status', dueKey: 'contract_po_due', label: 'Contract / PO', icon: '◫', description: 'Issue subcontract or purchase order to supplier' },
  { key: 'submittals', statusKey: 'submittal_status', dueKey: 'submittal_due', label: 'Submittals', icon: '◈', description: 'Supplier to submit drawings, specs, samples for approval' },
  { key: 'delivery', statusKey: 'delivery_status', dueKey: 'delivery_due', label: 'Delivery', icon: '→', description: 'Materials or works delivered to site' },
  { key: 'invoice', statusKey: 'invoice_status', dueKey: 'invoice_due', label: 'Invoice', icon: '£', description: 'Invoice received, checked, and processed for payment' },
];

export default function PostAward() {
  const { selectedProject } = useOutletContext();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadPackages();
  }, [selectedProject]);

  useEffect(() => {
    if (selectedPkg) loadChecklist(selectedPkg.id);
  }, [selectedPkg]);

  async function loadPackages() {
    setLoading(true);
    try {
      const data = await getPackages(selectedProject.id);
      // Show packages that are in post-award stages
      const postAwardPkgs = (data || []).filter(p => {
        const state = p.lifecycle_state || p.status;
        return ['award_approved', 'contract_po_pending', 'contract_po_issued',
          'submittals_pending', 'submittals_approved', 'delivery_scheduled',
          'delivered', 'invoice_pending', 'invoice_matched', 'closed', 'awarded'].includes(state);
      });
      setPackages(postAwardPkgs);
      if (postAwardPkgs.length > 0 && !selectedPkg) setSelectedPkg(postAwardPkgs[0]);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function loadChecklist(packageId) {
    try {
      const data = await getPackagePostAward(packageId);
      setChecklist(data);
    } catch (err) { console.error(err); }
  }

  async function handleUpdate(field, value) {
    if (!selectedPkg || !checklist) return;
    setSaving(true);
    try {
      const updated = { ...checklist, [field]: value };
      await updatePostAward(selectedPkg.id, updated);
      setChecklist(updated);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setSaving(false);
  }

  async function handleCreateChecklist() {
    if (!selectedPkg) return;
    setSaving(true);
    try {
      // Create checklist by updating with defaults
      await updatePostAward(selectedPkg.id, {
        contract_po_status: 'pending',
        submittal_status: 'pending',
        delivery_status: 'pending',
        invoice_status: 'pending',
        notes: ''
      });
      await loadChecklist(selectedPkg.id);
      setMessage({ type: 'success', text: 'Post-award checklist created' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setSaving(false);
  }

  // Calculate overall health
  const getHealth = () => {
    if (!checklist) return null;
    const stages = POST_AWARD_STAGES.map(s => ({
      ...s,
      status: checklist[s.statusKey],
      due: checklist[s.dueKey]
    }));
    const complete = stages.filter(s => s.status === 'complete').length;
    const overdue = stages.filter(s => s.status !== 'complete' && daysUntil(s.due) !== null && daysUntil(s.due) < 0).length;
    return { complete, overdue, total: stages.length };
  };

  const health = getHealth();

  if (loading) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Post-Award Tracking</div>
          <div className="page-subtitle">Track contract, submittals, delivery, and invoice for awarded packages</div>
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', display: 'flex', justifyContent: 'space-between' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5 }}>✕</button>
        </div>
      )}

      {packages.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <div className="empty-state-icon">◫</div>
          <h3>No awarded packages yet</h3>
          <p>Post-award tracking activates once packages are awarded and approved. Complete the procurement workflow first.</p>
          <button onClick={() => navigate('/packages')} className="btn btn-primary">Go to Packages →</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>

          {/* Package list */}
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Awarded Packages ({packages.length})</span>
            </div>
            {packages.map(pkg => {
              const isActive = selectedPkg?.id === pkg.id;
              const state = pkg.lifecycle_state || pkg.status;
              return (
                <div key={pkg.id} onClick={() => setSelectedPkg(pkg)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer',
                    background: isActive ? 'var(--color-teal-light)' : 'transparent',
                    borderLeft: `3px solid ${isActive ? 'var(--color-teal)' : 'transparent'}`,
                    borderBottom: '1px solid var(--color-border)',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--color-background)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg.name}</div>
                  <LifecyclePill state={state} />
                </div>
              );
            })}
          </div>

          {/* Checklist detail */}
          {selectedPkg && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Package header */}
              <div className="card">
                <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4 }}>{selectedPkg.name}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <LifecyclePill state={selectedPkg.lifecycle_state || selectedPkg.status} size="lg" />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{selectedPkg.trade_category}</span>
                    </div>
                  </div>
                  {health && (
                    <div style={{ display: 'flex', gap: 12 }}>
                      {[
                        { label: 'Complete', value: health.complete, color: 'var(--color-success)' },
                        { label: 'Overdue', value: health.overdue, color: health.overdue > 0 ? 'var(--color-error)' : 'var(--color-text-light)' },
                        { label: 'Total', value: health.total, color: 'var(--color-text-dark)' },
                      ].map(item => (
                        <div key={item.label} style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: item.color, fontFamily: 'var(--font-mono)' }}>{item.value}</div>
                          <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => navigate(`/packages/${selectedPkg.id}`)} className="btn btn-secondary btn-sm">
                    Open Package →
                  </button>
                </div>
              </div>

              {/* No checklist yet */}
              {!checklist ? (
                <div className="card">
                  <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem' }}>◫</div>
                    <h3>No post-award checklist yet</h3>
                    <p style={{ maxWidth: 400 }}>Create a checklist to track contract/PO, submittals, delivery, and invoice for this package.</p>
                    <button onClick={handleCreateChecklist} disabled={saving} className="btn btn-primary">
                      {saving ? 'Creating...' : '+ Create Checklist'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Progress bar */}
                  {health && (
                    <div style={{ padding: '12px 16px', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.8125rem' }}>
                        <span style={{ fontWeight: 600 }}>Overall Progress</span>
                        <span style={{ color: 'var(--color-text-mid)' }}>{health.complete} of {health.total} complete</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(health.complete / health.total) * 100}%`, background: health.overdue > 0 ? 'var(--color-error)' : 'var(--color-success)', borderRadius: 4, transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  )}

                  {/* Checklist stages */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {POST_AWARD_STAGES.map((stage, i) => {
                      const status = checklist[stage.statusKey] || 'pending';
                      const due = checklist[stage.dueKey];
                      const days = daysUntil(due);
                      const isOverdue = due && days !== null && days < 0 && status !== 'complete';
                      const isComplete = status === 'complete';

                      return (
                        <div key={stage.key} className="card" style={{
                          borderLeft: `3px solid ${isComplete ? 'var(--color-success)' : isOverdue ? 'var(--color-error)' : 'var(--color-border)'}`,
                        }}>
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                  <div style={{
                                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1rem', fontWeight: 700,
                                    background: isComplete ? 'var(--color-success)' : isOverdue ? 'var(--color-error-bg)' : 'var(--color-background)',
                                    color: isComplete ? '#fff' : isOverdue ? 'var(--color-error)' : 'var(--color-text-mid)',
                                    border: `1px solid ${isComplete ? 'var(--color-success)' : isOverdue ? 'var(--color-error)' : 'var(--color-border)'}`,
                                  }}>
                                    {isComplete ? '✓' : i + 1}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{stage.label}</div>
                                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{stage.description}</div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                {/* Due date */}
                                <div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>Due Date</div>
                                  <input
                                    type="date"
                                    className="form-input"
                                    value={due ? new Date(due).toISOString().split('T')[0] : ''}
                                    onChange={e => handleUpdate(stage.dueKey, e.target.value || null)}
                                    style={{ width: 160, fontSize: '0.8125rem', padding: '4px 8px' }}
                                  />
                                </div>

                                {/* Status */}
                                <div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>Status</div>
                                  <select
                                    className="form-select"
                                    value={status}
                                    onChange={e => handleUpdate(stage.statusKey, e.target.value)}
                                    style={{ width: 140 }}
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="complete">Complete</option>
                                    <option value="overdue">Overdue</option>
                                  </select>
                                </div>

                                {/* Due badge */}
                                <div style={{ paddingTop: 20 }}>
                                  <DueBadge date={due} status={status} />
                                </div>
                              </div>
                            </div>

                            {/* Overdue warning */}
                            {isOverdue && (
                              <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--color-error-bg)', borderRadius: 'var(--radius-md)', border: '1px solid #FCA5A5', fontSize: '0.8125rem', color: 'var(--color-error)', fontWeight: 500 }}>
                                ⚑ Overdue by {Math.abs(days)} day{Math.abs(days) !== 1 ? 's' : ''} — action required
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Notes */}
                  <div className="card">
                    <div className="card-header"><span style={{ fontWeight: 600 }}>Notes</span></div>
                    <div className="card-body">
                      <textarea
                        className="form-textarea"
                        value={checklist.notes || ''}
                        onChange={e => handleUpdate('notes', e.target.value)}
                        placeholder="Add post-award notes, actions, or reminders..."
                        style={{ minHeight: 100 }}
                      />
                    </div>
                  </div>

                  {/* Audit trail */}
                  <div className="card">
                    <div className="card-header"><span style={{ fontWeight: 600 }}>Activity</span></div>
                    <div style={{ padding: '0 16px 16px' }}>
                      <AuditTrail entityType="package" entityId={selectedPkg.id} maxItems={15} />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}